import * as React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import {
  fetchAdSummaryByAd,
  fetchAdSummaryBySubtheme,
  fetchAdSummaryByTheme,
  fetchAdSummaryFilterOptions,
  pauseAdSummaryAd,
} from '@src/api';
import api from '@src/api/clients';
import { formatDateToYMD } from '@src/utils/dateUtils';

import CreativeTable from './CreativeTable';
import AdPreviewDialog from './AdPreviewDialog';
import {
  VIEW_MODE_OPTIONS,
  CLIENT_FIELDS,
  MAX_CLIENT_META_PAGES,
  DECISION_KPI_OPTION,
  METRIC_COLUMNS,
  safeNumber,
  safeDiv,
  toOptionObjects,
  arraysEqual,
  computeDerivedMetrics,
  normalizeCampaignType,
  inferCreativeLabels,
  getAdAgeDays,
  formatAdAge,
  formatShortDate,
  formatPercentLabel,
  formatDeltaLabel,
  getLifecycleLabel,
  getKpiStage,
  getMaturityLabel,
  getPrimaryKpi,
  getFunnelBenchmarks,
  hasUsableBenchmarks,
  getDecisionCostMetrics,
  getBenchmarkDelta,
  getVerticalBenchmarkTargets,
  getCreativeScore,
  getBusinessScore,
  getOverallScore,
  getBenchmarkAdjustedScore,
  getConfidenceScore,
  getConfidenceLabel,
  getDirectionalDelta,
  getTrendLabel,
  getWhyDrivers,
  getTopWhyDriverChips,
  getWhyLabel,
  getCreativeVerdict,
  getDiagnosticLabel,
  getPausePriority,
  getBuildPriority,
  getPeerRankLabel,
  getStabilityLabel,
  getRecommendation,
  gateRecommendationByComConfidence,
  aggregatePatternLeaders,
  getBestComponentLeaders,
  getPatternRecommendationSummary,
  getRankedRows,
  buildAdHierarchy,
  getDateRangeDays,
  getLastThirtyDays,
  getRecentStartDate,
  getRecommendedLookbackGuidance,
  buildPauseAuditNote,
  getDimensionColumns,
  getRowLabel,
  buildPeerIndex,
  getPeersFromIndex,
  getSpendShareFromIndex,
  formatPriorityLabel,
} from './utils';

// ─── Client metadata loader ────────────────────────────────────────────────

async function fetchAllClientMetadata() {
  const allClients = [];
  let page = 1;
  while (page <= MAX_CLIENT_META_PAGES) {
    const response = await api.request({
      url: 'dashboard/clients/',
      method: 'GET',
      payload: { page, page_size: 250, ordering: 'name', fields: CLIENT_FIELDS },
    });
    const data = response?.data;
    if (Array.isArray(data)) { allClients.push(...data); break; }
    const results = Array.isArray(data?.results) ? data.results : [];
    allClients.push(...results);
    if (!data?.next) break;
    page += 1;
  }
  return allClients;
}

const getReportFetcher = (viewMode) => {
  if (viewMode === 'theme') return fetchAdSummaryByTheme;
  if (viewMode === 'subtheme') return fetchAdSummaryBySubtheme;
  return fetchAdSummaryByAd;
};

// ─── Main component ─────────────────────────────────────────────────────────

export default function ClientPerformanceAggregator() {
  const [defaultStart, defaultEnd] = React.useMemo(() => getLastThirtyDays(), []);
  const [startDate, setStartDate] = React.useState(defaultStart);
  const [endDate, setEndDate] = React.useState(defaultEnd);
  const [loading, setLoading] = React.useState(true);
  const [recentLoading, setRecentLoading] = React.useState(false);
  const [loadingClientMeta, setLoadingClientMeta] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [recentRows, setRecentRows] = React.useState([]);
  const [clientMetadata, setClientMetadata] = React.useState([]);
  const [selectedParentCompanies, setSelectedParentCompanies] = React.useState([]);
  const [selectedClients, setSelectedClients] = React.useState([]);
  const [viewMode, setViewMode] = React.useState('ad');
  const [selectedVerticals, setSelectedVerticals] = React.useState([]);
  const [selectedThemes, setSelectedThemes] = React.useState([]);
  const [selectedSubthemes, setSelectedSubthemes] = React.useState([]);
  const [selectedAdTypes, setSelectedAdTypes] = React.useState([]);
  const [selectedNotes, setSelectedNotes] = React.useState([]);
  const [showPausedAds, setShowPausedAds] = React.useState(false);
  const [backendCoverageFilter, setBackendCoverageFilter] = React.useState('all');
  const [previewRow, setPreviewRow] = React.useState(null);
  const [pauseNote, setPauseNote] = React.useState('');
  const [pauseLoading, setPauseLoading] = React.useState(false);
  const [expandedClients, setExpandedClients] = React.useState({});
  const [expandedCampaigns, setExpandedCampaigns] = React.useState({});
  const [expandedAdSets, setExpandedAdSets] = React.useState({});
  const [expandedQaRows, setExpandedQaRows] = React.useState({});
  const [qaRowDetails, setQaRowDetails] = React.useState({});
  const [qaRowLoading, setQaRowLoading] = React.useState({});
  const [qaRowErrors, setQaRowErrors] = React.useState({});
  const [filterOptions, setFilterOptions] = React.useState({
    verticals: [], themes: [], subthemes: [], adTypes: [], notes: [],
  });

  // ── Client metadata ─────────────────────────────────────────────────────

  React.useEffect(() => {
    let isMounted = true;
    const loadClients = async () => {
      setLoadingClientMeta(true);
      try {
        const clients = await fetchAllClientMetadata();
        if (!isMounted) return;
        setClientMetadata(clients.map((client = {}) => ({
          id: String(client.id),
          name: client.name || '',
          slug: client.slug || '',
          parentCompanyId: client.parent_company ? String(client.parent_company) : '__none__',
          parentCompanyName: client.parent_company_name || 'No Parent Company',
          hasMapping: Array.isArray(client.mapping_types) && client.mapping_types.length > 0,
        })));
      } catch (err) {
        console.error('Failed to load client metadata:', err);
        if (isMounted) { setClientMetadata([]); setError('Unable to load client metadata.'); }
      } finally {
        if (isMounted) setLoadingClientMeta(false);
      }
    };
    loadClients();
    return () => { isMounted = false; };
  }, []);

  const clientMetadataById = React.useMemo(() => {
    const next = new Map();
    clientMetadata.forEach((c) => next.set(c.id, c));
    return next;
  }, [clientMetadata]);

  const eligibleClients = React.useMemo(
    () => clientMetadata.filter((c) => !selectedParentCompanies.length || selectedParentCompanies.includes(c.parentCompanyId)),
    [clientMetadata, selectedParentCompanies]
  );

  const parentCompanyOptions = React.useMemo(() => {
    const deduped = new Map();
    eligibleClients.forEach((c) => {
      if (!deduped.has(c.parentCompanyId)) deduped.set(c.parentCompanyId, { id: c.parentCompanyId, name: c.parentCompanyName });
    });
    return Array.from(deduped.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [eligibleClients]);

  const clientOptions = React.useMemo(
    () => eligibleClients.map((c) => ({ id: c.id, name: c.name })).sort((a, b) => a.name.localeCompare(b.name)),
    [eligibleClients]
  );

  React.useEffect(() => {
    const allowed = new Set(parentCompanyOptions.map((o) => o.id));
    setSelectedParentCompanies((prev) => { const next = prev.filter((id) => allowed.has(id)); return arraysEqual(prev, next) ? prev : next; });
  }, [parentCompanyOptions]);

  React.useEffect(() => {
    const allowed = new Set(clientOptions.map((o) => o.id));
    setSelectedClients((prev) => { const next = prev.filter((id) => allowed.has(id)); return arraysEqual(prev, next) ? prev : next; });
  }, [clientOptions]);

  const effectiveClientIds = React.useMemo(() => {
    const scopedClients = selectedClients.length > 0
      ? eligibleClients.filter((c) => selectedClients.includes(c.id))
      : eligibleClients;
    return scopedClients.map((c) => c.id);
  }, [eligibleClients, selectedClients]);

  // ── Row mapper ──────────────────────────────────────────────────────────

  const mapSummaryRows = React.useCallback((summaryRows, referenceDate, currentViewMode) => (
    summaryRows
      .map((row = {}) => {
        const clientId = row.client_id ? String(row.client_id) : '';
        const clientMeta = clientId ? clientMetadataById.get(clientId) : null;
        const creativeLabels = inferCreativeLabels(row);
        const groupedHasBackendIntegration = safeNumber(row.backend_client_count) > 0;
        const hasBackendIntegration = Boolean(clientMeta?.hasMapping) || groupedHasBackendIntegration;

        const baseRow = {
          uuid: row.uuid || `${currentViewMode}-${row.theme || ''}-${row.subtheme || ''}-${row.ad_id || row.ad_name || row.client_id || ''}`,
          clientId, clientName: clientMeta?.name || row.client_name || row.client__name || '',
          parentCompanyId: clientMeta?.parentCompanyId || '__none__',
          parentCompanyName: clientMeta?.parentCompanyName || 'No Parent Company',
          hasMapping: hasBackendIntegration, hasBackendIntegration,
          verticalName: row.vertical || '',
          campaignType: normalizeCampaignType(row.campaign_type || ''),
          campaign: row.campaign || '', campaignId: row.campaign_id || '',
          themeName: row.theme || '', subthemeName: row.subtheme || '',
          adSetName: row.ad_set_name || '', adSetId: row.ad_set_id || '',
          adType: creativeLabels.adType,
          adName: row.ad_name || row.ad || '', adId: row.ad_id || '',
          notes: row.notes || '',
          isActive: row.active == null ? true : Boolean(row.active),
          activeStatusLabel: row.active == null || row.active ? 'Active' : 'Paused',
          facebookFormat: creativeLabels.facebookFormat,
          headline: row.headline || '', primaryText: row.primary_text || '',
          description: row.description || '', destinationUrl: row.destination_url || '',
          ctaType: row.cta_type || '',
          thumbnailUrl: row.thumbnail_url || '', imageUrl: row.image_url || '',
          videoId: row.video_id || '',
          clientCount: safeNumber(row.client_count),
          backendClientCount: safeNumber(row.backend_client_count),
          matureEndDate: row.mature_end_date || null,
          matureSpend: safeNumber(row.mature_spend_sum),
          matureLeads: safeNumber(row.mature_leads_sum),
          matureSelfBook: safeNumber(row.mature_self_books_sum),
          matureBooked: safeNumber(row.mature_booked_sum),
          matureScheduled: safeNumber(row.mature_scheduled_sum),
          matureIssues: safeNumber(row.mature_issues_sum),
          matureDemos: safeNumber(row.mature_demos_sum),
          matureGrossSold: safeNumber(row.mature_gross_sales_sum),
          matureNetSold: safeNumber(row.mature_net_sales_sum),
          matureNetAmount: safeNumber(row.mature_net_amount_sum),
          spend: safeNumber(row.total_spend_sum),
          impressions: safeNumber(row.impressions_sum),
          clicks: safeNumber(row.clicks_sum),
          landingPageViews: safeNumber(row.landing_page_views_sum),
          videoPlays3s: safeNumber(row.video_plays_3s_sum),
          thruplays: safeNumber(row.thruplays_sum),
          videoP25: safeNumber(row.video_p25_sum), videoP50: safeNumber(row.video_p50_sum),
          videoP75: safeNumber(row.video_p75_sum), videoP100: safeNumber(row.video_p100_sum),
          leads: safeNumber(row.total_leads_sum),
          selfBook: safeNumber(row.self_books_sum),
          booked: safeNumber(row.apps_booked_sum || row.booked_sum),
          scheduled: safeNumber(row.scheduled_sum),
          issues: safeNumber(row.issue_sum || row.issues_sum),
          demos: safeNumber(row.demo_sum || row.demos_sum),
          grossSold: safeNumber(row.gross_sold_sum || row.gross_sales_sum),
          netSold: safeNumber(row.net_sold_sum || row.net_sales_sum),
          netAmount: safeNumber(row.net_total_sum || row.net_amount_sum),
          saleCount: safeNumber(row.sale_count),
          avgDealValue: safeNumber(row.avg_deal_value),
          dealValueStddev: safeNumber(row.deal_value_stddev),
          dealValueCv: row.deal_value_cv == null ? null : safeNumber(row.deal_value_cv),
          dealValueStability: row.deal_value_stability || 'Unknown',
          dealValueStabilityDampener: safeNumber(row.deal_value_stability_dampener) || 0.8,
          adCreatedTime: row.ad_created_time || null,
        };

        const derived = computeDerivedMetrics(baseRow);
        const mergedRow = { ...baseRow, ...derived };
        const maturityLabel = getMaturityLabel(mergedRow);
        const adAgeDays = getAdAgeDays(baseRow.adCreatedTime, referenceDate);

        return {
          ...mergedRow,
          adAgeDays,
          adAgeLabel: formatAdAge(adAgeDays),
          maturityLabel,
          lifecycleLabel: getLifecycleLabel({
            ageDays: adAgeDays, spend: baseRow.spend, impressions: baseRow.impressions,
            leads: baseRow.leads, issues: baseRow.issues, demos: baseRow.demos, netSold: baseRow.netSold,
          }),
        };
      })
      .sort((left, right) => right.spend - left.spend)
  ), [clientMetadataById]);

  // ── Data fetching (Fix 6: main data loads first, recent deferred) ─────

  const buildPayload = React.useCallback(() => ({
    date_after: formatDateToYMD(startDate),
    date_before: formatDateToYMD(endDate),
    ordering: '-total_spend_sum',
    campaign_type: 'Broad',
    ...(selectedParentCompanies.length ? { parent_company_ids: selectedParentCompanies.join(',') } : {}),
    ...(effectiveClientIds.length ? { client_ids: effectiveClientIds.join(',') } : {}),
    ...(selectedVerticals.length ? { vertical: selectedVerticals.join(',') } : {}),
    ...(selectedThemes.length ? { theme: selectedThemes.join(',') } : {}),
    ...(selectedSubthemes.length ? { subtheme: selectedSubthemes.join(',') } : {}),
    ...(selectedAdTypes.length ? { ad_type: selectedAdTypes.join(',') } : {}),
    ...(selectedNotes.length ? { notes: selectedNotes.join(',') } : {}),
  }), [effectiveClientIds, endDate, selectedAdTypes, selectedNotes, selectedParentCompanies, selectedSubthemes, selectedThemes, selectedVerticals, startDate]);

  React.useEffect(() => {
    let isMounted = true;
    if (loadingClientMeta) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      setRecentRows([]);
      try {
        const summaryResp = await getReportFetcher(viewMode)(buildPayload());
        const summaryRows = Array.isArray(summaryResp?.data) ? summaryResp.data : Array.isArray(summaryResp?.data?.results) ? summaryResp.data.results : [];
        if (isMounted) setRows(mapSummaryRows(summaryRows, endDate, viewMode));
      } catch (err) {
        console.error('Failed to load ad performance aggregate:', err);
        if (isMounted) { setRows([]); setError('Unable to load aggregated ad performance data.'); }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [buildPayload, endDate, loadingClientMeta, mapSummaryRows, viewMode]);

  // Fix 6: Deferred recent-window fetch — loads after main data is ready
  React.useEffect(() => {
    let isMounted = true;
    if (loading || !rows.length || loadingClientMeta) return;
    const loadRecent = async () => {
      setRecentLoading(true);
      try {
        const recentStart = getRecentStartDate(startDate, endDate);
        const recentPayload = { ...buildPayload(), date_after: formatDateToYMD(recentStart), date_before: formatDateToYMD(endDate) };
        const recentSummaryResp = await getReportFetcher(viewMode)(recentPayload);
        const recentSummaryRows = Array.isArray(recentSummaryResp?.data) ? recentSummaryResp.data : Array.isArray(recentSummaryResp?.data?.results) ? recentSummaryResp.data.results : [];
        if (isMounted) setRecentRows(mapSummaryRows(recentSummaryRows, endDate, viewMode));
      } catch (err) {
        console.error('Failed to load recent ad performance:', err);
        if (isMounted) setRecentRows([]);
      } finally {
        if (isMounted) setRecentLoading(false);
      }
    };
    loadRecent();
    return () => { isMounted = false; };
  }, [buildPayload, endDate, loading, loadingClientMeta, mapSummaryRows, rows.length, startDate, viewMode]);

  // ── Filter options ──────────────────────────────────────────────────────

  React.useEffect(() => {
    let isMounted = true;
    if (loadingClientMeta) return;
    const loadFilterOptions = async () => {
      try {
        const payload = {
          date_after: formatDateToYMD(startDate), date_before: formatDateToYMD(endDate),
          ...(selectedParentCompanies.length ? { parent_company_ids: selectedParentCompanies.join(',') } : {}),
          ...(effectiveClientIds.length ? { client_ids: effectiveClientIds.join(',') } : {}),
        };
        const response = await fetchAdSummaryFilterOptions(payload);
        const data = response?.data || {};
        if (!isMounted) return;
        setFilterOptions({
          verticals: toOptionObjects(data.verticals || []),
          themes: toOptionObjects(data.themes || []),
          subthemes: toOptionObjects(data.subthemes || []),
          adTypes: toOptionObjects(data.ad_types || []),
          notes: toOptionObjects(data.notes || []),
        });
      } catch (err) {
        console.error('Failed to load ad summary filter options:', err);
        if (!isMounted) return;
        setFilterOptions({ verticals: [], themes: [], subthemes: [], adTypes: [], notes: [] });
      }
    };
    loadFilterOptions();
    return () => { isMounted = false; };
  }, [effectiveClientIds, endDate, loadingClientMeta, selectedParentCompanies, startDate]);

  // ── Filtered rows ─────────────────────────────────────────────────────

  const filteredRows = React.useMemo(
    () => rows.filter((row) => (
      (backendCoverageFilter !== 'backend_only' || row.hasBackendIntegration)
      && (viewMode !== 'ad' || showPausedAds || row.isActive)
    )),
    [backendCoverageFilter, rows, showPausedAds, viewMode]
  );

  const filteredRecentRows = React.useMemo(
    () => recentRows.filter((row) => (backendCoverageFilter !== 'backend_only' || row.hasBackendIntegration)),
    [backendCoverageFilter, recentRows]
  );

  // ── Decision engine (Fix 1: pre-indexed peers for O(1) lookups) ───────

  const decisionRows = React.useMemo(() => {
    if (viewMode !== 'ad') {
      return filteredRows.map((row) => {
        const confidenceScore = getConfidenceScore(row);
        return {
          ...row, confidenceScore,
          confidenceLabel: getConfidenceLabel(confidenceScore),
          spendShareLabel: '-', peerRankLabel: '-', stabilityLabel: '-', trendLabel: '-',
          whyLabel: 'Grouped summary row.',
        };
      });
    }

    // Fix 1: Build peer indexes once — O(n) — instead of filtering per row — O(n²)
    const peerIndex = buildPeerIndex(filteredRows);
    const recentPeerIndex = buildPeerIndex(filteredRecentRows);
    const recentRowsByUuid = new Map(filteredRecentRows.map((row) => [row.uuid, row]));

    return filteredRows.map((row) => {
      const primaryKpi = getPrimaryKpi(row);
      const recentRow = recentRowsByUuid.get(row.uuid) || row;
      const recentPrimaryKpi = getPrimaryKpi(recentRow);

      // O(1) peer lookups via index
      const clientVerticalPeers = getPeersFromIndex(row, peerIndex, 'client');
      const agencyVerticalPeers = getPeersFromIndex(row, peerIndex, 'agency');
      const backendClientVerticalPeers = clientVerticalPeers.filter((p) => p.hasBackendIntegration);
      const backendAgencyVerticalPeers = agencyVerticalPeers.filter((p) => p.hasBackendIntegration);

      // Fix 8: Vertical-aware benchmark targets
      const verticalTargets = getVerticalBenchmarkTargets(agencyVerticalPeers);
      const clientBenchmarks = getFunnelBenchmarks(backendClientVerticalPeers);
      const agencyBenchmarks = getFunnelBenchmarks(backendAgencyVerticalPeers);
      const selectedBenchmarks = hasUsableBenchmarks(clientBenchmarks) ? clientBenchmarks : agencyBenchmarks;

      const decisionCostMetrics = getDecisionCostMetrics(row, selectedBenchmarks);
      const { predictedCostOfMarketing, effectiveCostOfMarketing, comConfidenceBand, comConfidenceLabel, dealValueStability, dealValueStabilityDampener, decisionCostActualWeight, decisionCostLabel } = decisionCostMetrics;

      const decisionCostKpi = effectiveCostOfMarketing > 0
        ? { key: 'effectiveCostOfMarketing', label: decisionCostLabel, better: 'lower', benchmarkScope: 'client_vertical', isEligible: (c) => safeNumber(c.effectiveCostOfMarketing) > 0 }
        : primaryKpi;

      const enrichedClientVerticalPeers = clientVerticalPeers.map((p) => ({ ...p, ...getDecisionCostMetrics(p, selectedBenchmarks) }));
      const enrichedAgencyVerticalPeers = agencyVerticalPeers.map((p) => ({ ...p, ...getDecisionCostMetrics(p, selectedBenchmarks) }));

      const enrichedRow = { ...row, effectiveCostOfMarketing };
      const clientVerticalDelta = getBenchmarkDelta(enrichedRow, enrichedClientVerticalPeers, decisionCostKpi);
      const agencyVerticalDelta = getBenchmarkDelta(enrichedRow, enrichedAgencyVerticalPeers, decisionCostKpi);
      const benchmarkDelta = decisionCostKpi?.benchmarkScope === 'agency_vertical' ? agencyVerticalDelta : clientVerticalDelta;

      // Recent window benchmarks
      const recentClientVerticalPeers = getPeersFromIndex(recentRow, recentPeerIndex, 'client');
      const recentAgencyVerticalPeers = getPeersFromIndex(recentRow, recentPeerIndex, 'agency');
      const recentBackendClientPeers = recentClientVerticalPeers.filter((p) => p.hasBackendIntegration);
      const recentBackendAgencyPeers = recentAgencyVerticalPeers.filter((p) => p.hasBackendIntegration);
      const recentClientBenchmarks = getFunnelBenchmarks(recentBackendClientPeers);
      const recentAgencyBenchmarks = getFunnelBenchmarks(recentBackendAgencyPeers);
      const recentSelectedBenchmarks = hasUsableBenchmarks(recentClientBenchmarks) ? recentClientBenchmarks : recentAgencyBenchmarks;
      const recentDecisionCostMetrics = getDecisionCostMetrics(recentRow, recentSelectedBenchmarks);
      const recentEffectiveCostOfMarketing = recentDecisionCostMetrics.effectiveCostOfMarketing;
      const recentDecisionCostKpi = recentEffectiveCostOfMarketing > 0
        ? { key: 'effectiveCostOfMarketing', label: recentDecisionCostMetrics.decisionCostLabel, better: 'lower', benchmarkScope: 'client_vertical', isEligible: (c) => safeNumber(c.effectiveCostOfMarketing) > 0 }
        : recentPrimaryKpi;
      const enrichedRecentClientPeers = recentClientVerticalPeers.map((p) => ({ ...p, ...getDecisionCostMetrics(p, recentSelectedBenchmarks) }));
      const enrichedRecentAgencyPeers = recentAgencyVerticalPeers.map((p) => ({ ...p, ...getDecisionCostMetrics(p, recentSelectedBenchmarks) }));
      const recentClientDelta = getBenchmarkDelta({ ...recentRow, effectiveCostOfMarketing: recentEffectiveCostOfMarketing }, enrichedRecentClientPeers, recentDecisionCostKpi);
      const recentAgencyDelta = getBenchmarkDelta({ ...recentRow, effectiveCostOfMarketing: recentEffectiveCostOfMarketing }, enrichedRecentAgencyPeers, recentDecisionCostKpi);
      const recentBenchmarkDelta = recentDecisionCostKpi?.benchmarkScope === 'agency_vertical' ? recentAgencyDelta : recentClientDelta;

      const spendShare = getSpendShareFromIndex(row, peerIndex);
      const confidenceScore = getConfidenceScore(row);
      const confidenceLabel = getConfidenceLabel(confidenceScore);
      const trendLabel = getTrendLabel(benchmarkDelta, recentBenchmarkDelta, decisionCostKpi?.better || primaryKpi?.better || 'higher');

      const creativeBenchmarkDelta = getBenchmarkDelta(row, agencyVerticalPeers, { key: 'ctr', label: 'CTR', better: 'higher', benchmarkScope: 'agency_vertical', isEligible: (c) => c.impressions > 0 });
      const businessBenchmarkDelta = primaryKpi?.benchmarkScope === 'agency_vertical' ? agencyVerticalDelta : clientVerticalDelta;

      // Fix 8: Use vertical-aware targets for scoring
      const creativeScore = getBenchmarkAdjustedScore(getCreativeScore(row, verticalTargets.creative), creativeBenchmarkDelta, 'higher');
      const businessScore = getBenchmarkAdjustedScore(
        getBusinessScore({ ...row, effectiveCostOfMarketing }, verticalTargets.business),
        businessBenchmarkDelta,
        decisionCostKpi?.better || primaryKpi?.better || 'higher'
      );
      const overallScore = getOverallScore({ ...row, maturityLabel: row.maturityLabel }, creativeScore, businessScore, confidenceScore);
      const creativeVerdict = getCreativeVerdict({ ...row, maturityLabel: row.maturityLabel }, creativeScore, overallScore);
      const diagnosticLabel = getDiagnosticLabel({ ...row, maturityLabel: row.maturityLabel }, creativeScore, businessScore);

      const benchmarkScope = decisionCostKpi?.benchmarkScope === 'agency_vertical' ? 'agency_vertical' : 'client_vertical';
      const peerRowsForRank = benchmarkScope === 'agency_vertical' ? enrichedAgencyVerticalPeers : enrichedClientVerticalPeers;
      const whyDrivers = getWhyDrivers(enrichedRow, peerRowsForRank);

      const baseRecommendation = getRecommendation(enrichedRow, decisionCostKpi, benchmarkDelta);
      const overallRecommendation = gateRecommendationByComConfidence({
        recommendation: baseRecommendation, primaryKpi: decisionCostKpi,
        comConfidenceBand, dealValueStability, confidenceScore, whyDrivers,
      });
      const whyLabel = getWhyLabel({
        row, primaryKpi: decisionCostKpi || primaryKpi, benchmarkDelta, benchmarkScope,
        recommendation: overallRecommendation, diagnosticLabel, maturityLabel: row.maturityLabel,
        spendShare, confidenceLabel, whyDrivers,
      });
      const whyDriverChips = getTopWhyDriverChips(whyDrivers);

      const recentBaseRecommendation = getRecommendation(
        { ...recentRow, effectiveCostOfMarketing: recentEffectiveCostOfMarketing },
        recentDecisionCostKpi || recentPrimaryKpi, recentBenchmarkDelta
      );
      const recentRecommendation = gateRecommendationByComConfidence({
        recommendation: recentBaseRecommendation, primaryKpi: recentDecisionCostKpi || recentPrimaryKpi,
        comConfidenceBand: recentDecisionCostMetrics.comConfidenceBand,
        dealValueStability: recentDecisionCostMetrics.dealValueStability,
        confidenceScore, whyDrivers,
      });

      const peerRankLabel = getPeerRankLabel(enrichedRow, peerRowsForRank, decisionCostKpi || primaryKpi);
      const stabilityLabel = getStabilityLabel(recentRecommendation, overallRecommendation, trendLabel);
      const directionalDelta = getDirectionalDelta(benchmarkDelta, decisionCostKpi?.better || primaryKpi?.better || 'higher');

      const pausePriority = getPausePriority({ overallRecommendation, recentRecommendation, creativeVerdict, diagnosticLabel, confidenceScore, spendShare, directionalDelta });
      const buildPriority = getBuildPriority({ overallRecommendation, recentRecommendation, creativeVerdict, confidenceScore, spendShare, directionalDelta, stageLabel: row.maturityLabel });

      return {
        ...row,
        stageLabel: row.maturityLabel,
        creativeScore, businessScore, overallScore, creativeVerdict, diagnosticLabel,
        predictedCostOfMarketing,
        predictedCostOfMarketingLabel: predictedCostOfMarketing == null ? '-' : formatPercentLabel(predictedCostOfMarketing),
        effectiveCostOfMarketing,
        effectiveCostOfMarketingLabel: effectiveCostOfMarketing == null ? '-' : formatPercentLabel(effectiveCostOfMarketing),
        comConfidenceBand, comConfidenceLabel, dealValueStability, dealValueStabilityDampener, decisionCostActualWeight,
        confidenceScore, confidenceLabel,
        spendShare, spendShareLabel: formatPercentLabel(spendShare),
        pausePriority, pausePriorityLabel: formatPriorityLabel(pausePriority),
        buildPriority, buildPriorityLabel: formatPriorityLabel(buildPriority),
        peerRankLabel, stabilityLabel,
        primaryKpiLabel: primaryKpi?.label || '-',
        clientVerticalDelta, agencyVerticalDelta,
        clientVerticalDeltaLabel: formatDeltaLabel(clientVerticalDelta),
        agencyVerticalDeltaLabel: formatDeltaLabel(agencyVerticalDelta),
        decisionBenchmarkDelta: benchmarkDelta,
        recentRecommendation, overallRecommendation, trendLabel,
        whyLabel, whyDriverChips,
      };
    });
  }, [filteredRecentRows, filteredRows, viewMode]);

  // ── Derived data ──────────────────────────────────────────────────────

  const totals = React.useMemo(() => {
    const base = decisionRows.reduce((acc, row) => {
      acc.spend += safeNumber(row.spend); acc.leads += safeNumber(row.leads);
      acc.selfBook += safeNumber(row.selfBook); acc.booked += safeNumber(row.booked);
      acc.scheduled += safeNumber(row.scheduled); acc.issues += safeNumber(row.issues);
      acc.demos += safeNumber(row.demos); acc.grossSold += safeNumber(row.grossSold);
      acc.netSold += safeNumber(row.netSold); acc.netAmount += safeNumber(row.netAmount);
      return acc;
    }, { spend: 0, leads: 0, selfBook: 0, booked: 0, scheduled: 0, issues: 0, demos: 0, grossSold: 0, netSold: 0, netAmount: 0 });
    return computeDerivedMetrics(base);
  }, [decisionRows]);

  const adHierarchy = React.useMemo(
    () => (viewMode === 'ad' ? buildAdHierarchy(decisionRows) : []),
    [decisionRows, viewMode]
  );

  React.useEffect(() => {
    if (viewMode !== 'ad') return;
    setExpandedClients((prev) => {
      const next = {};
      adHierarchy.forEach((g) => { next[g.key] = prev[g.key] ?? true; });
      return Object.keys(next).length === Object.keys(prev).length && Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next;
    });
    setExpandedCampaigns((prev) => {
      const next = {};
      adHierarchy.forEach((g) => g.campaigns.forEach((c) => { next[c.key] = prev[c.key] ?? true; }));
      return Object.keys(next).length === Object.keys(prev).length && Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next;
    });
    setExpandedAdSets((prev) => {
      const next = {};
      adHierarchy.forEach((g) => g.campaigns.forEach((c) => c.adSets.forEach((a) => { next[a.key] = prev[a.key] ?? true; })));
      return Object.keys(next).length === Object.keys(prev).length && Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next;
    });
  }, [adHierarchy, viewMode]);

  // ── QA ────────────────────────────────────────────────────────────────

  const fetchQaBackendRecords = React.useCallback(async (row) => {
    const response = await api.request({
      url: 'dashboard/backend-data/', method: 'GET',
      payload: { client: row.clientId, ad_id: row.adId, date_field: 'lead_created_date', date_after: formatDateToYMD(startDate), date_before: formatDateToYMD(endDate), ordering: '-lead_created_date', page_size: 100 },
    });
    const data = response?.data;
    const results = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    return { records: results, totalCount: safeNumber(data?.count ?? results.length) };
  }, [endDate, startDate]);

  const handleToggleQaRow = React.useCallback(async (row) => {
    if (!row?.uuid) return;
    const rowKey = row.uuid;
    if (expandedQaRows[rowKey]) { setExpandedQaRows((p) => ({ ...p, [rowKey]: false })); return; }
    setExpandedQaRows((p) => ({ ...p, [rowKey]: true }));
    if (qaRowDetails[rowKey] || qaRowLoading[rowKey]) return;
    setQaRowLoading((p) => ({ ...p, [rowKey]: true }));
    setQaRowErrors((p) => ({ ...p, [rowKey]: '' }));
    try {
      const detail = await fetchQaBackendRecords(row);
      setQaRowDetails((p) => ({ ...p, [rowKey]: detail }));
    } catch (err) {
      const message = err?.response?.data?.detail || err?.message || 'Unable to load backend QA records.';
      setQaRowErrors((p) => ({ ...p, [rowKey]: message }));
    } finally {
      setQaRowLoading((p) => ({ ...p, [rowKey]: false }));
    }
  }, [expandedQaRows, fetchQaBackendRecords, qaRowDetails, qaRowLoading]);

  React.useEffect(() => {
    setExpandedQaRows({}); setQaRowDetails({}); setQaRowLoading({}); setQaRowErrors({});
  }, [startDate, endDate, viewMode]);

  // ── Decision board data ───────────────────────────────────────────────

  const topPerformers = React.useMemo(() => getRankedRows(decisionRows, DECISION_KPI_OPTION, 'best'), [decisionRows]);
  const bottomPerformers = React.useMemo(() => getRankedRows(decisionRows, DECISION_KPI_OPTION, 'worst'), [decisionRows]);
  const bestThemes = React.useMemo(() => aggregatePatternLeaders(decisionRows, 'themeName').sort((a, b) => b.rankingScore - a.rankingScore).slice(0, 5), [decisionRows]);
  const worstThemes = React.useMemo(() => aggregatePatternLeaders(decisionRows, 'themeName').sort((a, b) => a.rankingScore - b.rankingScore).slice(0, 5), [decisionRows]);
  const bestSubthemes = React.useMemo(() => aggregatePatternLeaders(decisionRows, 'subthemeName').sort((a, b) => b.rankingScore - a.rankingScore).slice(0, 5), [decisionRows]);
  const worstSubthemes = React.useMemo(() => aggregatePatternLeaders(decisionRows, 'subthemeName').sort((a, b) => a.rankingScore - b.rankingScore).slice(0, 5), [decisionRows]);
  const bestFormats = React.useMemo(() => getBestComponentLeaders(decisionRows, 'facebookFormat'), [decisionRows]);

  const actionQueues = React.useMemo(() => {
    if (viewMode !== 'ad') return { topKeepRunning: [], topPause: [], keepTesting: [], buildMore: [] };
    const rankedByPause = [...decisionRows].sort((a, b) => b.pausePriority - a.pausePriority || b.spend - a.spend);
    const rankedByBuild = [...decisionRows].sort((a, b) => b.buildPriority - a.buildPriority || b.spend - a.spend);
    return {
      topKeepRunning: rankedByBuild.filter((r) => r.overallRecommendation === 'Keep Running' && r.buildPriority >= 35).slice(0, 5),
      topPause: rankedByPause.filter((r) => r.pausePriority >= 35).slice(0, 5),
      keepTesting: rankedByBuild.filter((r) => r.overallRecommendation === 'Test Longer' || r.confidenceLabel === 'Low').slice(0, 5),
      buildMore: rankedByBuild.filter((r) => r.buildPriority >= 45 && r.creativeVerdict !== 'Weak').slice(0, 5),
    };
  }, [decisionRows, viewMode]);

  const dimensionColumns = React.useMemo(() => getDimensionColumns(viewMode), [viewMode]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleDateRangeChange = (event) => {
    const selection = event?.selection || {};
    if (selection.startDate) setStartDate(selection.startDate);
    if (selection.endDate) setEndDate(selection.endDate);
  };

  const handleOpenPreview = React.useCallback((row) => { setPreviewRow(row); setPauseNote(buildPauseAuditNote(row)); }, []);
  const handleClosePreview = React.useCallback(() => { setPreviewRow(null); setPauseNote(''); setPauseLoading(false); }, []);
  const toggleClientExpanded = React.useCallback((key) => { setExpandedClients((p) => ({ ...p, [key]: !(p[key] ?? true) })); }, []);
  const toggleCampaignExpanded = React.useCallback((key) => { setExpandedCampaigns((p) => ({ ...p, [key]: !(p[key] ?? true) })); }, []);
  const toggleAdSetExpanded = React.useCallback((key) => { setExpandedAdSets((p) => ({ ...p, [key]: !(p[key] ?? true) })); }, []);

  const handlePauseAd = React.useCallback(async () => {
    if (!previewRow?.clientId || !previewRow?.adId || pauseLoading) return;
    setPauseLoading(true); setError(null);
    try {
      await pauseAdSummaryAd({
        client_id: previewRow.clientId, ad_id: previewRow.adId,
        final_note: pauseNote || buildPauseAuditNote(previewRow),
        row_snapshot: previewRow,
      });
      const applyPaused = (currentRows) => currentRows.map((r) => (
        r.clientId !== previewRow.clientId || r.adId !== previewRow.adId ? r : { ...r, isActive: false, activeStatusLabel: 'Paused' }
      ));
      setRows((p) => applyPaused(p));
      setRecentRows((p) => applyPaused(p));
      setPreviewRow((p) => p ? { ...p, isActive: false, activeStatusLabel: 'Paused' } : p);
    } catch (err) {
      const responseData = err?.response?.data;
      const detail = responseData?.detail || responseData?.ad_id || responseData?.client_id || 'Unable to pause the selected Meta ad.';
      setError(Array.isArray(detail) ? detail.join(' ') : String(detail));
    } finally { setPauseLoading(false); }
  }, [pauseLoading, pauseNote, previewRow]);

  // ── Computed labels ───────────────────────────────────────────────────

  const pageSubheader = {
    ad: 'Broad campaigns work best at the ad level. Creative evaluation combines Facebook delivery, lead progression, and business-outcome guidance. Clients without backend integrations stop at lead/self-book stages and are excluded from later-stage benchmark math.',
    theme: 'Theme-level creative pattern rollup for broad creative analysis. Use this to spot reusable patterns across the current scope.',
    subtheme: 'Subtheme-level creative pattern rollup for broad creative analysis. Use this to find more specific winners worth briefing into the next round.',
  }[viewMode];

  const backendCapableRows = decisionRows.filter((r) => r.hasBackendIntegration).length;
  const parsedThemeCoverage = decisionRows.length ? safeDiv(decisionRows.filter((r) => r.themeName).length, decisionRows.length) : 0;
  const parsedSubthemeCoverage = decisionRows.length ? safeDiv(decisionRows.filter((r) => r.subthemeName).length, decisionRows.length) : 0;
  const matureCohortCutoffLabel = React.useMemo(() => { const r = filteredRows.find((row) => row.matureEndDate); return formatShortDate(r?.matureEndDate); }, [filteredRows]);
  const currentRangeDays = getDateRangeDays(startDate, endDate);
  const recommendedRangeGuidance = React.useMemo(
    () => getRecommendedLookbackGuidance({ rows: filteredRows, recentRows: filteredRecentRows, currentRangeDays }),
    [currentRangeDays, filteredRecentRows, filteredRows]
  );

  // ─── Render helper for leader cards ───────────────────────────────────

  const renderLeaderCard = (title, subheader, items, renderItem) => (
    <Card sx={{ flex: 1 }}>
      <CardHeader title={title} subheader={subheader} />
      <CardContent>
        <Stack spacing={1.5}>
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No data yet.</Typography>
          ) : items.map(renderItem)}
        </Stack>
      </CardContent>
    </Card>
  );

  // ─── JSX ──────────────────────────────────────────────────────────────

  return (
    <Page title="Creative Performance" fullWidth>
      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}

        {/* ── Header + Filters ─────────────────────────────────────────── */}
        <Card>
          <CardContent>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', lg: 'center' }} justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.05 }}>Creative Performance</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 980 }}>{pageSubheader}</Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <Box sx={{ px: 1.5, py: 1, borderRadius: 2.5, bgcolor: 'success.main', color: 'common.white', minWidth: 200 }}>
                    <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>Date Range</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{formatDateToYMD(startDate)} - {formatDateToYMD(endDate)}</Typography>
                  </Box>
                  {viewMode === 'ad' ? (
                    <Box sx={{ px: 1.5, py: 1, borderRadius: 2.5, bgcolor: 'info.light', color: 'info.contrastText', minWidth: 240 }}>
                      <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                        <Tooltip title="Shorter windows are better for high-volume accounts. Longer windows are better when ad-level volume is split across many creatives." arrow>
                          <Box component="span" sx={{ cursor: 'help' }}>Recommended Range</Box>
                        </Tooltip>
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{recommendedRangeGuidance.label}</Typography>
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, mt: 0.25 }}>{recommendedRangeGuidance.reason}</Typography>
                    </Box>
                  ) : null}
                  <Box sx={{ px: 1.5, py: 1, borderRadius: 2.5, bgcolor: 'grey.100', color: 'text.primary', minWidth: 160 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Scope</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{decisionRows.length} {viewMode === 'ad' ? 'rows' : viewMode === 'theme' ? 'themes' : 'subthemes'}</Typography>
                  </Box>
                </Stack>
              </Stack>

              <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', xl: 'center' }}>
                <Box sx={{ minWidth: { xs: '100%', xl: 320 } }}>
                  <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateRangeChange} style={{ width: '100%' }} />
                  {viewMode === 'ad' ? <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>{recommendedRangeGuidance.detailText}</Typography> : null}
                </Box>
                <Box sx={{ display: 'grid', gap: 1.25, flex: 1, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(220px, 1fr))', xl: 'repeat(4, minmax(180px, 1fr))' } }}>
                  <Autocomplete multiple size="small" options={parentCompanyOptions.map((o) => ({ label: o.name, value: o.id }))} value={parentCompanyOptions.filter((o) => selectedParentCompanies.includes(o.id)).map((o) => ({ label: o.name, value: o.id }))} onChange={(_, v) => setSelectedParentCompanies(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Parent Companies" />} />
                  <Autocomplete multiple size="small" options={clientOptions.map((o) => ({ label: o.name, value: o.id }))} value={clientOptions.filter((o) => selectedClients.includes(o.id)).map((o) => ({ label: o.name, value: o.id }))} onChange={(_, v) => setSelectedClients(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Clients" />} />
                </Box>
              </Stack>

              <Box sx={{ p: 1.25, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', xl: 'center' }} justifyContent="space-between">
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }} flexWrap="wrap">
                    <ToggleButtonGroup value={viewMode} exclusive size="small" onChange={(_, v) => { if (v) setViewMode(v); }} sx={{ '& .MuiToggleButton-root': { border: 0, px: 1.6, py: 0.8, borderRadius: 2, fontWeight: 700 } }}>
                      {VIEW_MODE_OPTIONS.map((o) => <ToggleButton key={o.key} value={o.key}>{o.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                    {viewMode === 'ad' ? (
                      <ToggleButtonGroup value={showPausedAds ? 'all' : 'active'} exclusive size="small" onChange={(_, v) => { if (v) setShowPausedAds(v === 'all'); }} sx={{ '& .MuiToggleButton-root': { border: 0, px: 1.6, py: 0.8, borderRadius: 2, fontWeight: 700 } }}>
                        <ToggleButton value="active">Active Only</ToggleButton>
                        <ToggleButton value="all">Include Paused</ToggleButton>
                      </ToggleButtonGroup>
                    ) : null}
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <ToggleButtonGroup value={backendCoverageFilter} exclusive size="small" onChange={(_, v) => { if (v) setBackendCoverageFilter(v); }} sx={{ '& .MuiToggleButton-root': { border: 0, px: 1.6, py: 0.8, borderRadius: 2, fontWeight: 700 } }}>
                      <ToggleButton value="all">All Clients</ToggleButton>
                      <ToggleButton value="backend_only">Backend Only</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="body2" color="text.secondary">Showing {decisionRows.length} {viewMode === 'ad' ? 'rows' : viewMode === 'theme' ? 'themes' : 'subthemes'}</Typography>
                    {matureCohortCutoffLabel ? <Typography variant="body2" color="text.secondary">Backend KPIs use mature lead cohorts through {matureCohortCutoffLabel}.</Typography> : null}
                  </Stack>
                </Stack>
              </Box>

              <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(220px, 1fr))', xl: 'repeat(5, minmax(170px, 1fr))' } }}>
                <Autocomplete multiple size="small" options={filterOptions.verticals} value={filterOptions.verticals.filter((o) => selectedVerticals.includes(o.value))} onChange={(_, v) => setSelectedVerticals(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Vertical" />} />
                <Autocomplete multiple size="small" options={filterOptions.themes} value={filterOptions.themes.filter((o) => selectedThemes.includes(o.value))} onChange={(_, v) => setSelectedThemes(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Theme" />} />
                <Autocomplete multiple size="small" options={filterOptions.subthemes} value={filterOptions.subthemes.filter((o) => selectedSubthemes.includes(o.value))} onChange={(_, v) => setSelectedSubthemes(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Subtheme" />} />
                <Autocomplete multiple size="small" options={filterOptions.adTypes} value={filterOptions.adTypes.filter((o) => selectedAdTypes.includes(o.value))} onChange={(_, v) => setSelectedAdTypes(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Ad Type" />} />
                <Autocomplete multiple size="small" options={filterOptions.notes} value={filterOptions.notes.filter((o) => selectedNotes.includes(o.value))} onChange={(_, v) => setSelectedNotes(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Notes" />} />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* ── Summary cards ────────────────────────────────────────────── */}
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', xl: 'center' }}>
              <Box sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: 'grey.100' }}>
                <Typography variant="overline" color="text.secondary">Spend</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}><DollarFormatter value={totals.spend} /></Typography>
                <Typography variant="body2" color="text.secondary">Across {decisionRows.length} {viewMode === 'ad' ? 'rows' : viewMode}.</Typography>
              </Box>
              <Box sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: 'grey.100' }}>
                <Typography variant="overline" color="text.secondary">Decision KPI</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{DECISION_KPI_OPTION.label}</Typography>
                <Typography variant="body2" color="text.secondary">Auto-switches between predicted and actual CoM based on maturity-adjusted lead cohorts.</Typography>
              </Box>
              <Box sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: 'grey.100' }}>
                <Typography variant="overline" color="text.secondary">Backend Coverage</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{backendCapableRows}</Typography>
                <Typography variant="body2" color="text.secondary">Rows with downstream benchmark eligibility.</Typography>
              </Box>
              <Box sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: 'grey.100' }}>
                <Typography variant="overline" color="text.secondary">Theme Coverage</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{formatPercentLabel(parsedThemeCoverage)}</Typography>
                <Typography variant="body2" color="text.secondary">Subtheme coverage: {formatPercentLabel(parsedSubthemeCoverage)}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* ── Decision boards ──────────────────────────────────────────── */}
        {viewMode === 'ad' ? (
          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
            {renderLeaderCard('Top Keep-Running Ads', 'Best current ads to leave on based on decision CoM, confidence, and meaningful spend.', actionQueues.topKeepRunning, (row) => (
              <Box key={`keep-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box><Typography sx={{ fontWeight: 600 }}>{row.adName}</Typography><Typography variant="body2" color="text.secondary">{row.clientName} &bull; {row.verticalName || 'Unassigned'} &bull; {row.spendShareLabel} share</Typography></Box>
                <Typography sx={{ fontWeight: 700 }}>{row.overallRecommendation}</Typography>
              </Box>
            ))}
            {renderLeaderCard('Top Pause Candidates', 'Highest wasted-spend risk based on weak signal, confidence, and spend share.', actionQueues.topPause, (row) => (
              <Box key={`pause-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box><Typography sx={{ fontWeight: 600 }}>{row.adName}</Typography><Typography variant="body2" color="text.secondary">{row.clientName} &bull; {row.verticalName || 'Unassigned'} &bull; {row.spendShareLabel} share</Typography></Box>
                <Typography sx={{ fontWeight: 700 }}>{row.pausePriorityLabel}</Typography>
              </Box>
            ))}
            {renderLeaderCard('Keep Testing', 'Ads with interesting signal but not enough confidence yet to force a decision.', actionQueues.keepTesting, (row) => (
              <Box key={`test-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box><Typography sx={{ fontWeight: 600 }}>{row.adName}</Typography><Typography variant="body2" color="text.secondary">{row.clientName} &bull; {row.verticalName || 'Unassigned'} &bull; {row.confidenceLabel} confidence</Typography></Box>
                <Typography sx={{ fontWeight: 700 }}>{row.overallRecommendation}</Typography>
              </Box>
            ))}
            {renderLeaderCard('Build More Like This', 'Creative patterns worth reusing when you brief the next round of ads.', actionQueues.buildMore, (row) => (
              <Box key={`build-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box><Typography sx={{ fontWeight: 600 }}>{row.adName}</Typography><Typography variant="body2" color="text.secondary">{row.clientName} &bull; {row.themeName || 'No Theme'} &bull; {row.subthemeName || 'No Subtheme'} &bull; {row.facebookFormat || 'Unknown'}</Typography></Box>
                <Typography sx={{ fontWeight: 700 }}>{row.buildPriorityLabel}</Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
            {renderLeaderCard('Best Grouped Winners', 'Top grouped patterns by automatic decision CoM in the active scope.', topPerformers, (row) => (
              <Box key={`gw-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box><Typography sx={{ fontWeight: 600 }}>{getRowLabel(row, viewMode)}</Typography><Typography variant="body2" color="text.secondary">{row.clientCount ? `${row.clientCount} clients \u2022 ` : ''}<DollarFormatter value={row.spend} /></Typography></Box>
                <Typography sx={{ fontWeight: 700 }}>{renderMetricVal(row[DECISION_KPI_OPTION.key], METRIC_COLUMNS.find((c) => c.key === DECISION_KPI_OPTION.key)?.type)}</Typography>
              </Box>
            ))}
            {renderLeaderCard('Grouped Pause Risks', 'Patterns that look weak enough to stop producing or deprioritize.', bottomPerformers, (row) => (
              <Box key={`gr-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box><Typography sx={{ fontWeight: 600 }}>{getRowLabel(row, viewMode)}</Typography><Typography variant="body2" color="text.secondary">{row.clientCount ? `${row.clientCount} clients \u2022 ` : ''}<DollarFormatter value={row.spend} /></Typography></Box>
                <Typography sx={{ fontWeight: 700, color: 'error.main' }}>{renderMetricVal(row[DECISION_KPI_OPTION.key], METRIC_COLUMNS.find((c) => c.key === DECISION_KPI_OPTION.key)?.type)}</Typography>
              </Box>
            ))}
          </Stack>
        )}

        {/* ── Theme / Subtheme / Format leaders ────────────────────────── */}
        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
          {renderLeaderCard('Best Themes', 'Highest-confidence theme patterns worth reusing in the current scope.', bestThemes, (theme) => (
            <Box key={`bt-${theme.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{theme.name}</Typography>
                <Typography variant="body2" color="text.secondary">{theme.count} ads &bull; <DollarFormatter value={theme.spend} /> &bull; {theme.confidenceLabel} confidence</Typography>
                <Typography variant="caption" color="text.secondary">{theme.effectiveCostOfMarketingAvg != null ? <>Avg Decision CoM <PercentageFormatter value={theme.effectiveCostOfMarketingAvg} /> &bull; </> : null}{getPatternRecommendationSummary(theme)}</Typography>
              </Box>
              <Typography sx={{ fontWeight: 700 }}>{theme.rankingScore}</Typography>
            </Box>
          ))}
          {renderLeaderCard('Formats', 'Ranked format performance with confidence and action mix.', bestFormats, (format) => (
            <Box key={`bf-${format.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{format.name || 'Unknown'}</Typography>
                <Typography variant="body2" color="text.secondary">{format.count} ads &bull; <DollarFormatter value={format.spend} /> &bull; {format.confidenceLabel} confidence</Typography>
                <Typography variant="caption" color="text.secondary">{format.effectiveCostOfMarketingAvg != null ? <>Avg Decision CoM <PercentageFormatter value={format.effectiveCostOfMarketingAvg} /> &bull; </> : null}{getPatternRecommendationSummary(format)}</Typography>
              </Box>
              <Typography sx={{ fontWeight: 700 }}>{format.rankingScore}</Typography>
            </Box>
          ))}
        </Stack>

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
          {renderLeaderCard('Worst Themes', 'Lowest-confidence or weakest theme patterns to avoid or rewrite.', worstThemes, (theme) => (
            <Box key={`wt-${theme.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{theme.name}</Typography>
                <Typography variant="body2" color="text.secondary">{theme.count} ads &bull; <DollarFormatter value={theme.spend} /> &bull; {theme.confidenceLabel} confidence</Typography>
                <Typography variant="caption" color="text.secondary">{theme.effectiveCostOfMarketingAvg != null ? <>Avg Decision CoM <PercentageFormatter value={theme.effectiveCostOfMarketingAvg} /> &bull; </> : null}{getPatternRecommendationSummary(theme)}</Typography>
              </Box>
              <Typography sx={{ fontWeight: 700, color: 'error.main' }}>{theme.rankingScore}</Typography>
            </Box>
          ))}
          {renderLeaderCard('Best Subthemes', 'Top parsed subthemes worth briefing into the next round of ads.', bestSubthemes, (subtheme) => (
            <Box key={`bs-${subtheme.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{subtheme.name}</Typography>
                <Typography variant="body2" color="text.secondary">{subtheme.count} ads &bull; <DollarFormatter value={subtheme.spend} /> &bull; {subtheme.confidenceLabel} confidence</Typography>
                <Typography variant="caption" color="text.secondary">{subtheme.effectiveCostOfMarketingAvg != null ? <>Avg Decision CoM <PercentageFormatter value={subtheme.effectiveCostOfMarketingAvg} /> &bull; </> : null}{getPatternRecommendationSummary(subtheme)}</Typography>
              </Box>
              <Typography sx={{ fontWeight: 700 }}>{subtheme.rankingScore}</Typography>
            </Box>
          ))}
        </Stack>

        {renderLeaderCard('Worst Subthemes', 'Subthemes that are repeatedly weak enough to retire, rewrite, or deprioritize.', worstSubthemes, (subtheme) => (
          <Box key={`ws-${subtheme.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{subtheme.name}</Typography>
              <Typography variant="body2" color="text.secondary">{subtheme.count} ads &bull; <DollarFormatter value={subtheme.spend} /> &bull; {subtheme.confidenceLabel} confidence</Typography>
              <Typography variant="caption" color="text.secondary">{subtheme.effectiveCostOfMarketingAvg != null ? <>Avg Decision CoM <PercentageFormatter value={subtheme.effectiveCostOfMarketingAvg} /> &bull; </> : null}{getPatternRecommendationSummary(subtheme)}</Typography>
            </Box>
            <Typography sx={{ fontWeight: 700, color: 'error.main' }}>{subtheme.rankingScore}</Typography>
          </Box>
        ))}

        {/* ── Main table ───────────────────────────────────────────────── */}
        <Card>
          <CardHeader
            title={viewMode === 'ad' ? 'Creative Evaluation Table' : viewMode === 'theme' ? 'Theme Evaluation Table' : 'Subtheme Evaluation Table'}
            subheader="Decision-first creative evaluation with spend, funnel progression, and business outcome metrics."
          />
          <CardContent sx={{ p: 0 }}>
            <CreativeTable
              viewMode={viewMode}
              decisionRows={decisionRows}
              adHierarchy={adHierarchy}
              dimensionColumns={dimensionColumns}
              totals={totals}
              loading={loading}
              loadingClientMeta={loadingClientMeta}
              startDate={startDate}
              endDate={endDate}
              onPreviewOpen={handleOpenPreview}
              onToggleQa={handleToggleQaRow}
              expandedClients={expandedClients}
              expandedCampaigns={expandedCampaigns}
              expandedAdSets={expandedAdSets}
              expandedQaRows={expandedQaRows}
              qaRowDetails={qaRowDetails}
              qaRowLoading={qaRowLoading}
              qaRowErrors={qaRowErrors}
              toggleClientExpanded={toggleClientExpanded}
              toggleCampaignExpanded={toggleCampaignExpanded}
              toggleAdSetExpanded={toggleAdSetExpanded}
            />
          </CardContent>
        </Card>

        {/* ── Preview / Pause dialog ───────────────────────────────────── */}
        <AdPreviewDialog
          previewRow={previewRow}
          pauseNote={pauseNote}
          pauseLoading={pauseLoading}
          onPauseNoteChange={setPauseNote}
          onPause={handlePauseAd}
          onClose={handleClosePreview}
        />
      </Stack>
    </Page>
  );
}

// Small helper used in grouped decision board
function renderMetricVal(value, type) {
  if (type === 'currency') return <DollarFormatter value={value} />;
  if (type === 'ratio') return <PercentageFormatter value={value} />;
  return safeNumber(value).toLocaleString();
}
